// React Dependencies
import React, { useEffect, useState, useReducer } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { TextField, TextareaAutosize, makeStyles } from '@material-ui/core';

// Internal Dependencies
import Colours from '../../../styles/colours';
import ApiService from '../../../services/apiService';

// Components
import Loading from '../../../components/Loading';
import AreaMap from '../../../components/AreaMap';
import MText from '../../../components/Common/MText';
import MButton from '../../../components/MButton';

const defaultCallout = {
  calloutTitle:   '',
  calloutContent: '',
  calloutCoord:   {},
};

const useStyles = makeStyles((theme) => ({
  container: {
    width: '85%',

    [theme.breakpoints.down('sm')]: {
      width: '94%',
    },
  },
}));

function MapSettings({ match }) {
  const classes = useStyles();
  const [boundary, setBoundary] = useState([]);
  const [mapBoundId, setMapBoundId] = useState(null);
  const [colour, setColour] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [callout, setCallout] = useReducer((state, action) => ({ ...state, ...action }), defaultCallout);
  const [upperLowerCoord, setUpperLowerCoord] = useState(null);

  const initMap = async () => {
    try {
      setIsLoading(true);
      const { pageId, loyaltyCardId } = match.params;
      const { mapBound, participateOutlets } = await ApiService.getLoyaltyMapOutlets({ pageId, loyaltyCardId });

      setBoundary(mapBound.coords);
      setOutlets(_.map(participateOutlets, (outlet) => ({
        stampName: outlet.stampName,
        logo:      outlet.stampImage || outlet.linkPageLogo,
        coords:    { lat: outlet.lat, lng: outlet.lng },
      })));

      setColour(`#${mapBound.colour}`);
      setMapBoundId(mapBound.id);

      setCallout({
        calloutTitle:   mapBound.calloutTitle,
        calloutContent: mapBound.calloutContent,
        calloutCoord:   mapBound.calloutCoord,
      });

      setUpperLowerCoord({
        lowerLat: mapBound.lowerLat,
        lowerLng: mapBound.lowerLng,
        upperLat: mapBound.upperLat,
        upperLng: mapBound.upperLng,
      });
    } catch (err) {
      console.log('Error in initMap: ', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initMap();
  }, []);

  const onCalloutChange = (e) => {
    const { name, value } = e.target;
    setCallout({ [name]: value  });
  };

  const onUpdateLoyaltyMap = async () => {
    try {
      setIsLoading(true);
      const updateDict = {
        calloutTitle:   callout.calloutTitle,
        calloutContent: callout.calloutContent,
        calloutCoord:   callout.calloutCoord,
      };

      await ApiService.updateLoyaltyMap({
        mapBoundId,
        pageId:        match.params.pageId,
        loyaltyCardId: match.params.loyaltyCardId,
      }, updateDict);

      toast.success('Loyalty card map updated successfully');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const updateCalloutCoord = (coord) => {
    const event = {
      target: {
        name:  'calloutCoord',
        value: coord,
      },
    };

    onCalloutChange(event);
  };

  return (
    <div className="container-fluid py-3 pl-4" style={{ backgroundColor: Colours.White }}>
      <div className={`mx-2 my-3 ${classes.container}`}>
        { isLoading && <Loading /> }
        <MText colour={Colours.Gray1} size="0.813rem" text={`Loyalty Card ID: ${match.params.loyaltyCardId}`} />
        <MText className="my-1" size="1rem" bold text="Map Settings" />

        {
          !isLoading && (
            <>
              <div>
                <MText
                  variant="subtitle1"
                  color="inherit"
                  style={{ fontSize: '0.813rem' }}
                  text="Enter the title and content that will be shown in Loyalty Card's Map"
                />
                <div className="geographic_boundary__formContentContainer">
                  <div className="geographic_boundary__formContentSubContainer" style={{ width: '100%' }}>
                    <div className="mt-3">
                      <MText size="0.813rem" text="Title" />
                      <TextField
                        fullWidth
                        variant="outlined"
                        value={callout.calloutTitle}
                        inputProps={{ style: { fontSize: '0.813rem' } }}
                        className="bg-white"
                        name="calloutTitle"
                        onChange={onCalloutChange}
                      />
                    </div>

                    <div className="mt-3">
                      <MText className="mb-2" size="0.813rem" text="Content (Max 100 characters)" />
                      <TextareaAutosize
                        variant="outlined"
                        minRows={6}
                        className="w-100"
                        style={{
                          fontSize:     '0.813rem',
                          borderRadius: '4px',
                          padding:      '12px',
                        }}
                        name="calloutContent"
                        value={callout.calloutContent}
                        onChange={onCalloutChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <AreaMap
                isEditableCalloutCoord
                outlets={outlets}
                boundaries={boundary}
                colour={colour}
                upperLowerCoord={upperLowerCoord}
                heading="Select callout coord in map for this Loyalty Card"
                width="100%"
                calloutCoord={callout.calloutCoord}
                calloutTitle={callout.calloutTitle}
                calloutContent={callout.calloutContent}
                onUpdateCalloutCoord={updateCalloutCoord}
              />
              <div className="d-flex mt-3 mb-3">
                <MButton onClick={onUpdateLoyaltyMap} variant="contained" colour="primary" text="Update" />
              </div>
            </>
          )
        }
      </div>
    </div>
  );
}

MapSettings.propTypes = {
  match: PropTypes.shape.isRequired,
};

export default MapSettings;
